import React, { useEffect } from 'react';
import { PageSteps } from '../../sales-common/NewHeader';
import { Button, IconType, Icon, Tooltip } from '@repo/ui/components';
import { IOrder, isPcodeEnableForCurrentBrand, MAKE } from 'next-common';
import { VehicleContext } from '../../../../utils';
import { IStepMenuData, menuTitles, VarebilWizardSteps } from './SalesWizardMenu';
import { MissingItem } from '../../sales-common/DynamicCols/MissingItem';
import { cn, TooltipPositionsEnum } from 'types';
import { ICentralisedProperties, isDemoOrder, isStockOrder } from '../../../../common';
import { ValidationErrorCode, getErrorMessageForError } from '../../../notifications';
import { IPcodeValidation } from '../SalesWizard';
import { isEmpty } from 'lodash';
import { useAppSelector } from '@store/hooks';
import { selectConfiguration } from '@store/selectors';
const _ = require('lodash');

export interface ISalesWizardButtonMenu extends ICentralisedProperties {
  order?: IOrder;
  calculationId?: string;
  activeStep: number;
  isUnavailablePimData: boolean;
  orderId: string;
  menuLoading: boolean;
  isStockSaleOrder?: boolean;
  stepData?: IStepMenuData;
  allStepData?: IStepMenuData[];
  bottomButton?: boolean;
  onClickSigning?(stepData?: IStepMenuData): void;
  onClick(stepData?: IStepMenuData | any): void;
  isChange?: boolean;
  isFactoryOrder?: boolean;
  isVarebilEnabled: boolean;
  disablePCodePage?: boolean;
  invalidPcodesValidation?: IPcodeValidation;
  isUsedCar?: boolean;
  isMerBrand?: boolean;
  isForeignBrand?: boolean;
}

const getShowButton = (isVarebilEnabled) => {
  const showButton = [PageSteps.START, PageSteps.PACKAGES, PageSteps.FINANACE];
  if (isVarebilEnabled) {
    showButton.splice(1, 0, PageSteps.IMPORTEROPTIONS);
  }
  return showButton;
};

export const SalesWizardButtonMenu = (props: ISalesWizardButtonMenu) => {
  const [missingItems, setMissingItems] = React.useState<string[]>([]);
  const [validationText, setValidationText] = React.useState<string>('');
  const { getVehicleData } = React.useContext(VehicleContext) ?? {};
  const isOrderStockType = isStockOrder(props?.order?.lead?.orderType);
  const isOrderDemoType = isDemoOrder(props?.order?.lead?.orderType);
  const showButton = getShowButton(props.isVarebilEnabled);
  const activeRoute = props.allStepData?.find((route) => route?.routeIsActive)?.routeName;
  const invalidPcodes = props?.invalidPcodesValidation;
  const configurations = useAppSelector(selectConfiguration);
  useEffect(() => {
    const items: string[] = [];
    if (!props.order?.customer?.bosCustomer) items?.push(MissingItem.Customer);
    if (!props.order?.vehicles || props.order?.vehicles?.length < 1) items?.push(MissingItem.Car);
    if (!props.calculationId && !props?.order?.chosenOffer?.id) items?.push(MissingItem.Offer);
    setMissingItems(items);
  }, [props.order, props.calculationId]);

  useEffect(() => {
    if (isOrderStockType) {
      if (!props?.order?.chosenOffer?.id) {
        setValidationText(getErrorMessageForError(ValidationErrorCode.SIGNING_VEHICLE_VALIDATION));
      }
    } else if (invalidPcodes?.errorMessage) {
      setValidationText(invalidPcodes?.errorMessage);
    } else if (missingItems?.length) {
      setValidationText(`Velg ${missingItems?.join(' og ')} før du kan signere`);
    } else setValidationText(null);
  }, [missingItems]);

  const isSelectedVehicleAvailable = () => {
    const isValidForeignCar = props.isForeignBrand && !isEmpty(props.order?.vehicles?.[0]?.serialNo);
    if (
      props?.order?.externalPricing?.make === MAKE.Mercedes ||
      props.isStockSaleOrder ||
      isValidForeignCar ||
      (props.isUsedCar && !isEmpty(props.order?.vehicles?.[0]?.serialNo))
    ) {
      return true;
    }
    if (props?.orderId) {
      return getVehicleData()?.modelId && getVehicleData()?.variantId;
    }
    return false;
  };

  const getClassName = (): string =>
    (activeRoute === VarebilWizardSteps[VarebilWizardSteps.Finance] && missingItems?.length) ||
    (props.activeStep === PageSteps.START && !isSelectedVehicleAvailable())
      ? 'main-sign-btn'
      : 'main-btn';

  const getValueAtIndex = (menuTitles, routeName: string) => {
    if (props.activeStep === 0 && props.disablePCodePage) {
      return menuTitles.VehicleAccessories;
    } else {
      return menuTitles[routeName];
    }
  };

  const nextStepNameIndex = props.activeStep + 1;
  const route = props?.allStepData?.[nextStepNameIndex];
  const nextStepName = getValueAtIndex(menuTitles, route?.routeName);

  const className = getClassName();
  const isUnavailablePimData = props?.isUnavailablePimData || className === 'main-sign-btn';

  return (
    <div className="flex items-center justify-between">
      {showButton?.includes(props?.activeStep) && (
        <div className={`${className} nextButton`}>
          {activeRoute === VarebilWizardSteps[VarebilWizardSteps.Finance] ||
          (activeRoute === VarebilWizardSteps[VarebilWizardSteps.ImporterOptions] && isOrderStockType) ||
          (activeRoute === VarebilWizardSteps[VarebilWizardSteps.Vehicle] &&
            isOrderStockType &&
            (props.disablePCodePage || !isPcodeEnableForCurrentBrand(configurations, { order: props?.order }))) ||
          (activeRoute === VarebilWizardSteps[VarebilWizardSteps.VehicleAccessories] && isOrderDemoType) ? (
            <Tooltip
              small
              content={validationText}
              enabledCondition={
                isOrderStockType
                  ? !props?.order?.chosenOffer?.id
                  : missingItems?.length > 0 || invalidPcodes?.isInvalidPcodes
              }
              className={cn(props.bottomButton ? 'bottomButton' : '')}
              tooltipPosition={props?.bottomButton ? TooltipPositionsEnum.TOP : TooltipPositionsEnum.BOTTOM}
            >
              <Button
                variant="teal"
                isLoading={!props?.order}
                disabled={
                  isOrderStockType
                    ? props?.isChange && props?.isFactoryOrder
                      ? true
                      : !(props?.order?.chosenOffer?.id && props?.order?.vehicles?.length > 0)
                    : isUnavailablePimData || missingItems?.length > 0 || invalidPcodes?.isInvalidPcodes
                }
                onClick={() => props?.onClickSigning(props.stepData)}
              >
                <span className="text-base/[175%] font-extrabold">Neste steg: Signering</span>
                <Icon icon={IconType.NewNextArrowRight} width={24} height={24} fill="currentColor" />
              </Button>
            </Tooltip>
          ) : (
            <Button
              variant={'teal'}
              disabled={isUnavailablePimData}
              isLoading={!props?.order ? true : props?.menuLoading}
              onClick={props?.onClick}
            >
              <span className="text-base/[175%] font-extrabold">{`Neste steg: ${nextStepName}`}</span>
              <Icon icon={IconType.NewNextArrowRight} width={24} height={24} fill="currentColor" />
            </Button>
          )}
        </div>
      )}
    </div>
  );
};
